<template>
  <div>
    <div :class="`${need_show_tutorial ? 'overlay-blur' : ''}`"></div>
    <z-form
      ref="form"
      v-slot="{ failed }"
      :class="`${need_show_tutorial ? 'z-card border-0 tutorial-form' : 'z-card border-0'}`"
      @submit.native.prevent="handler_submit"
    >
      <div class="row align-items-center">
        <div class="col-6">
          <div
            :id="tutorial_steps[0].id"
            :title="tutorial_steps[counter].text"
            :class="`${tutorial_steps[counter].id === 'sender' ? `hint hint--${counter}` : ''}`"
          >
            <mailings-form-sender-select @input="on_sender_select" />
          </div>
          <div class="z-card mt-4">
            <h4>{{ $t('mailer.mailing.form.block.content.title') }}</h4>

            <div class="z-card__body">
              <div
                :id="tutorial_steps[1].id"
                :class="`${tutorial_steps[counter].id === 'name' ? `hint hint--${counter}` : ''}`"
                :title="tutorial_steps[counter].text"
              >
                <z-form-group class="row">
                  <div class="col-3 text-right">
                    {{ $t('mailer.mailing.form.field.title.title') }}
                  </div>
                  <z-form-input class="col-9" :rules="{ required: true, max: 255 }">
                    <i-input
                      v-model="form.name"
                      :placeholder="$t('mailer.mailing.form.field.title.placeholder')"
                    />
                  </z-form-input>
                </z-form-group>
              </div>

              <mailings-form-macros-select @input="set_macros" />

              <div
                :id="tutorial_steps[2].id"
                :class="`${tutorial_steps[counter].id === 'body' ? `hint hint--${counter}` : ''}`"
                :title="tutorial_steps[counter].text"
              >
                <z-form-group class="row">
                  <div class="col-3 text-right">
                    {{ $t('mailer.mailing.form.field.body.title') }}
                  </div>
                  <z-form-input class="col-9" :rules="{ required: true, sms_count: 5 }">
                    <template>
                      <message-input ref="sms-input" v-model="form.body" />
                    </template>
                  </z-form-input>
                </z-form-group>
              </div>
            </div>
          </div>

          <div class="z-card mt-4">
            <h4>{{ $t('mailer.mailing.form.block.recipients.title') }}</h4>

            <div class="z-card__body recipient-wrap">
              <z-form-input :rules="{ required_recipient: true }">
                <recipient-input v-model="form.recipients" :range_from_one="true" />
              </z-form-input>
              <div
                :id="tutorial_steps[3].id"
                :class="`${
                  tutorial_steps[counter].id === 'recipient' ? `hint hint--${counter}` : ''
                }`"
                :title="tutorial_steps[counter].text"
              >
                <div class="row mt-4">
                  <div
                    :class="`${
                      tutorial_steps[counter].id === 'recipient'
                        ? `col text-right tutorial-button`
                        : 'col text-right'
                    }`"
                  >
                    <i-button type="default" size="large" @click="add_recipients">
                      {{ $t('shared.component.recipient_form.button.add_recipients') }}
                    </i-button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="z-card mt-4">
            <h4>{{ $t('mailer.mailing.form.block.date.title') }}</h4>

            <div class="z-card__body">
              <div class="row align-items-center">
                <div class="ml-3">
                  <checkbox v-model="is_delay_send" @on-change="get_current_date">
                    <span>{{ $t('mailer.mailing.form.block.date.action') }}</span>
                  </checkbox>
                </div>
                <div class="col" style="min-height: 32px">
                  <z-form-input
                    :rules="{ required: is_delay_send, old_date: is_delay_send ? offset : false }"
                  >
                    <date-picker
                      v-show="is_delay_send"
                      v-model="form.start_at"
                      :options="date_options"
                      type="datetime"
                      :transfer="true"
                    />
                  </z-form-input>
                </div>
                <tooltip
                  v-show="is_delay_send"
                  placement="top"
                  min-width="500"
                  max-width="500"
                  theme="light"
                  :content="$t('mailer.tooltips.delayed_until')"
                >
                  <icon size="18" type="ios-help-circle-outline" />
                </tooltip>
              </div>
            </div>
          </div>
        </div>

        <div class="col-6">
          <iphone :body="form.body" :sender_name="sender_name" />
          <div class="mailing-description">
            <template v-if="!is_loading_cost">
              <h6 v-if="calculated_mailing_cost.count" class="mailing-description__count">
                {{ $t('shared.component.recipient_form.send.description') }}
                {{
                  $tc('shared.component.recipient_form.number_count', calculated_mailing_cost.count)
                }}
              </h6>
              <h5 v-if="calculated_mailing_cost.cost" class="mailing-description__cost">
                {{
                  $t('shared.component.recipient_form.send.cost', {
                    cost: $numeral.currency(calculated_mailing_cost.cost)
                  })
                }}
              </h5>
              <div
                :id="tutorial_steps[4].id"
                :class="`${tutorial_steps[counter].id === 'ok' ? `hint hint--${counter}` : ''}`"
                :title="tutorial_steps[counter].text"
              >
                <div class="hint-wrap hint-wrap--bottom">
                  <i-button
                    html-type="submit"
                    type="primary"
                    class="mt-4"
                    style="width: 200px"
                    :disabled="failed"
                  >
                    {{ $t('shared.component.recipient_form.send.send') }}
                  </i-button>
                </div>
              </div>
              <template v-if="failed">
                <div class="mt-4 mailing-description__error">
                  {{ $t('validator.std_form_error') }}
                </div>
              </template>
            </template>

            <spin v-if="is_loading_cost" size="large" fix />
          </div>
        </div>
      </div>
      <div v-if="need_show_tutorial && tutorial_steps[counter].show_info" class="general-hint">
        <div class="general-hint--border flex">
          <img class="mr-4" src="/img/other/info.svg" />
          <span>
            СМС-рассылка-удобный способ сообщить своим клиентам об акциях или проинформировать о
            статусе заказа. Создайте свою первую СМС-рассылку
          </span>
        </div>
      </div>
      <div
        v-if="need_show_tutorial"
        class="tutorial-navigation-wrap tutorial-navigation-wrap--close"
        @click="close_tutorial"
      ></div>
      <i-button
        v-if="need_show_tutorial && counter + 1 < tutorial_steps.length"
        class="tutorial-navigation-wrap--pagination-button"
        type="default"
        size="small"
        @click="forward_tutorial"
      >
        {{ $t('shared.component.button.next') }}
      </i-button>
      <div
        v-if="need_show_tutorial"
        class="tutorial-navigation-wrap tutorial-navigation-wrap--pagination"
      >
        <div v-if="counter !== 0" class="arrow--back" @click="backward_tutorial"></div>
        {{ counter + 1 }} / {{ tutorial_steps.length }}
        <div
          v-if="counter + 1 < tutorial_steps.length"
          class="arrow"
          @click="forward_tutorial"
        ></div>
      </div>
    </z-form>
  </div>
</template>

<script>
  import MailingsFormMacrosSelect from '@mailer/components/mailings/mailings-form-macros-select';
  import MailingsFormSenderSelect from '@mailer/components/mailings/mailings-form-sender-select';
  import MessageInput from '@shared/components/input/input-sms-textarea';
  import RecipientInput from '@shared/components/input/recipients/input-recipients';
  import cloneDeep from 'lodash-es/cloneDeep';
  import { MailingListService } from '@mailer/services/MailingListService';
  import { PageServerValidationResponseMixin } from '@shared/mixins/page-server-validation-response';
  import { RecipientModalService } from '@mailer/modals/RecipientModalService';
  import { toBackendDateWithTime } from '@shared/helpers/date_format';
  import { validate } from 'vee-validate';
  import Iphone from '@mailer/components/mailings/mailings-form-iphone';
  import moment from 'moment-timezone';

  export default {
    components: {
      Iphone,
      MailingsFormMacrosSelect,
      MailingsFormSenderSelect,
      MessageInput,
      RecipientInput
    },
    mixins: [PageServerValidationResponseMixin],
    data: () => ({
      date_options: {
        disabledDate(date) {
          return date && date.valueOf() < Date.now() - 86400000;
        }
      },
      need_show_tutorial: false,
      counter: 0,
      tutorial_steps: [
        { id: 'sender', text: 'Выберите имя отправителя', show_info: true },
        { id: 'name', text: 'Придумайте название рассылки' },
        { id: 'body', text: 'Введите текст сообщения' },
        {
          id: 'recipient',
          text: 'Добавьте номер получателя, например укажите свой номер'
        },
        { id: 'ok', text: 'Ваша первая рассылка готова, нажмите “Отправить”' }
      ],

      current_sender: null,
      calculated_mailing_cost: {
        count: 0,
        cost: 0
      },
      is_loading_cost: false,
      is_delay_send: false,
      form: {
        start_at: null,
        sender: null,
        name: '',
        body: '',
        recipients: {
          start_at: null,
          recipients: [],
          include: [],
          exclude: []
        }
      }
    }),
    computed: {
      offset() {
        return moment.tz(this.$user.timezone).utcOffset();
      },
      sender_name() {
        try {
          return this.current_sender.name;
        } catch (e) {
          return '';
        }
      },
      get_position() {
        const el = document.getElementById(this.tutorial_steps[this.counter].id);
        const rect = el.getBoundingClientRect();
        const top = rect.top;
        return { y: top };
      }
    },
    created() {
      this.need_show_tutorial = !!this.$route.params?.tutorial;
    },
    watch: {
      form: {
        handler() {
          this.handler_change_form();
        },
        deep: true
      }
    },

    methods: {
      // ...mapActions('mailer/mailings/form/recipients-rang/', ['addStartRange']),

      backward_tutorial() {
        this.counter -= 1;
        window.scrollBy({
          top: this.get_position.y - 20,
          behavior: 'smooth'
        });
      },
      forward_tutorial() {
        if (this.counter < this.tutorial_steps.length) {
          this.counter += 1;
          window.scrollBy({
            top: this.get_position.y - 80,
            behavior: 'smooth'
          });
        }
      },
      close_tutorial() {
        this.need_show_tutorial = false;
        this.counter = 0;
      },
      get_current_date() {
        if (this.is_delay_send) {
          this.form.start_at = moment()
            .add(1, 'hours')
            .utcOffset(this.offset)
            .format('YYYY-MM-DD HH:mm:ss');
        } else {
          this.form.start_at = null;
        }
      },
      handler_change_form() {
        this.validate_for_loading_cost()
          .then(() => {
            this.calculate_cost();
          })
          .catch(() => {});
      },
      validate_for_loading_cost() {
        return new Promise(async (resolve, reject) => {
          try {
            const recipient_validate = await validate(this.form.recipients, 'required_recipient');
            const is_recipients = recipient_validate.valid;
            const { sender: is_sender, name: is_name, body: is_body } = this.form;
            const is_validate = is_recipients && is_sender && is_name && is_body;
            if (is_validate) {
              resolve(is_validate);
            } else {
              reject({ message: 'not valid' });
            }
          } catch (e) {
            reject(e);
          }
        });
      },
      calculate_cost() {
        this.is_loading_cost = true;

        MailingListService.getCost(this.get_params())
          .then((result) => {
            if (result) {
              this.calculated_mailing_cost.cost = result.cost || 0;
              this.calculated_mailing_cost.count = result.recipients || 0;
            } else {
              const mailing_cost = 0;
              console.debug(mailing_cost);
            }
          })
          .catch((errors) => {
            this.$noty.server_error(errors);
            this.calculated_mailing_cost.cost = 0;
            this.calculated_mailing_cost.count = 0;
          })
          .finally(() => {
            this.is_loading_cost = false;
          });
      },
      get_params() {
        const { sender, body, name, recipients, start_at } = this.form;

        const params = {
          sender,
          body
        };

        let recipientsParam = {};

        recipientsParam = {
          extra_recipients: recipients.recipients?.map((i) => ({ number: i })),
          include: recipients.include?.map((i) => ({
            id: i.id,
            range: { from: i.range[0], to: i.range[1] }
          })),
          exclude: recipients.exclude?.map((i) => ({
            id: i.id,
            range: { from: i.range[0], to: i.range[1] }
          }))
        };

        params.mailing = name;
        params.recipients = recipientsParam;

        if (start_at) {
          params.start_at = toBackendDateWithTime(start_at);
        }
        return params;
      },

      handler_submit() {
        this.$refs.form.validate().then((is_valid) => {
          if (is_valid) {
            const params = this.get_params();

            MailingListService.createItem(params)
              .then(() => {
                this.$router.push({ name: 'mailer.mailing.index' });
              })
              .catch((error) => {
                if (
                  error?.response?.data?.message ===
                  'The limit is exceeded, most likely you have accumulated mailings in the queue for a larger amount than the remaining balance'
                ) {
                  this.$noty.error(this.$t('mailer.message.noty.balance'));
                } else {
                  this.check_server_validation(error);
                }
              });
          }
        });
      },

      set_macros(val) {
        if (val) {
          const macros = ['{', ...val.split(''), '}'];
          const cursor_position = this.$refs['sms-input'].cursor_position();
          if (cursor_position === 0) {
            this.form.body += `{${val}}`;
            return;
          }
          const body_arr = this.form.body.split('');
          body_arr.splice(cursor_position, 0, ...macros);
          this.form.body = body_arr.join('');
          this.$nextTick(() => (this.current_macros = null));
        }
      },

      on_sender_select(value) {
        this.current_sender = value;
        if (value && value.name) {
          this.form.sender = value.name;
        } else {
          this.form.sender = value;
        }
      },

      add_recipients() {
        RecipientModalService.select({
          value: cloneDeep(this.form.recipients),
          range_from_one: true
        }).on('success', (res) => this.$set(this.form, 'recipients', res));
      }
    }
  };
</script>

<style lang="scss">
  .recipient-wrap {
    .form-input__error--absolute {
      // bottom: -80px;
    }
  }
  .mailing-description {
    margin-top: $spacer * 2;
    width: 380px;
    text-align: right;
    min-height: 125px;
    position: relative;
    margin-left: auto;
    margin-right: auto;
    &__error {
      color: red;
      position: absolute;
      right: 0;
    }
    h6 {
      font-size: 16px;
      margin-bottom: 10px;
    }

    h5 {
      font-size: 20px;
    }
  }

  .tutorial-form {
    .hint {
      &--0 {
        &:before {
          right: -65px;
          top: 63%;
        }
        &:after {
          right: -170px;
          top: 80%;
        }
      }
      &--1 {
        &:before {
          right: -90px;
          top: 45%;
        }
        &:after {
          right: -230px;
          top: 75%;
        }
      }
      &--2 {
        &:before {
          right: -90px;
          top: 45%;
        }
        &:after {
          right: -200px;
          top: 65%;
        }
      }

      &--3 {
        &:before {
          right: -77px;
          top: 35%;
        }
        &:after {
          right: -250px;
          top: 84%;
          width: 220px;
        }
      }

      &--4 {
        z-index: 100;
        &:before {
          right: 115px;
          top: 125%;
          transform: rotate(220deg);
        }

        &:after {
          right: -50px;
          top: 150%;
          width: 200px;
        }
      }
    }
  }
</style>
